import {
  channelTypes,
  assetCategoryTypes,
  CLIENT_STORAGE_KEY
} from "../constants";

const defaultAssetMap = {
  [assetCategoryTypes.CART]: new Map(),
  [assetCategoryTypes.SHOPPING_LIST]: new Map()
};

/**
 * CommerceAsset class provides methods to manage and interact with commerce-related assets,
 * such as shopping lists and carts, stored in the client storage.
 */
export class CommerceAsset {
  constructor(storage, locale) {
    this.storage = storage;
    this.storageKey = this._getClientStorageKey(locale);
    const { assets, scope } = this._loadAssetFromStorage(this.storageKey);
    this.scope = scope;
    this.assets = assets;
    this.cache = new Map();

    if (typeof window !== "undefined") {
      window.addEventListener("storage", (event) =>
        this._handleStorageUpdate(event)
      );
    }
  }

  /**
   * Handles updates to the browser's localStorage.
   * Updates the assets and scope if the storage key matches.
   * @param {StorageEvent} event - The storage event triggered by changes to localStorage.
   */
  _handleStorageUpdate(event) {
    if (event.key != this.storageKey) return;
    const { scope, assets } = this._loadAssetFromStorage(this.storageKey);
    this.assets = assets;
    this.scope = scope;
  }

  /**
   * Loads assets from the client storage.
   * If no assets are found, returns default assets.
   * @param {string} storageKey - The key to retrieve assets from storage.
   * @returns {Object} An object containing the loaded assets and scope.
   */
  _loadAssetFromStorage(storageKey) {
    // Read the existing asset from storage, server just return null
    const storedAsset =
      typeof window !== "undefined" ? this.storage.getItem(storageKey) : null;
    // if assets is not present in storage, then assign default empty Maps
    if (!storedAsset) {
      return {
        assets: {
          [assetCategoryTypes.CART]: new Map(),
          [assetCategoryTypes.SHOPPING_LIST]: new Map()
        },
        scope: false
      };
    }

    const scope = storedAsset.scope;

    const carts = new Map();
    const lists = new Map();

    try {
      // Restore CART structure
      if (storedAsset?.[assetCategoryTypes.CART]) {
        Object.values(storedAsset[assetCategoryTypes.CART]).forEach((cart) => {
          carts.set(cart.id, cart);
        });
      }

      // Restore SHOPPING_LIST structure
      if (storedAsset?.[assetCategoryTypes.SHOPPING_LIST]) {
        Object.values(storedAsset[assetCategoryTypes.SHOPPING_LIST]).forEach(
          (list) => {
            lists.set(list.id, list);
          }
        );
      }

      const assets = {
        [assetCategoryTypes.CART]: carts,
        [assetCategoryTypes.SHOPPING_LIST]: lists
      };

      return {
        scope,
        assets
      };
    } catch (error) {
      throw new Error(`Failed to load asset from storage, ${error?.message}`);
    }
  }
  /**
   * Retrieves the client storage key based on the locale.
   * @param {string} locale - The locale for the assets.
   * @returns {string} The client storage key.
   */
  _getClientStorageKey = (locale) => {
    try {
      if (!locale) throw new Error("Parameter 'locale' is required");
      const { countryCode } = locale;
      return `${CLIENT_STORAGE_KEY}_${countryCode}`;
    } catch (error) {
      throw new Error(`Failed to generate storage key, ${error?.message}`);
    }
  };

  /**
   * Retrieves all cart assets as an array.
   * @returns {Array<Object>} An array of cart assets.
   */
  get carts() {
    return Array.from(this.assets[assetCategoryTypes.CART].values());
  }

  /**
   * Retrieves all shopping list assets as an array.
   * @returns {Array<Object>} An array of shopping list assets.
   */
  get shoppingLists() {
    return Array.from(this.assets[assetCategoryTypes.SHOPPING_LIST].values());
  }

  /**
   * Retrieves the current asset scope.
   * @returns {string|null} The current scope of the assets, or null if not set.
   */
  get assetScope() {
    return this.scope;
  }
  /**
   * Removes a cart asset by its ID.
   * Deletes the cart from the asset map and writes the changes to storage.
   * @param {string} id - The ID of the cart to remove.
   */
  removeCart(id) {
    this.assets[assetCategoryTypes.CART].delete(id);
    this.write();
  }

  /**
   * Removes a shopping list asset by its ID.
   * Deletes the shopping list from the asset map and writes the changes to storage.
   * @param {string} id - The ID of the shopping list to remove.
   */
  removeShoppingList(id) {
    this.assets[assetCategoryTypes.SHOPPING_LIST].delete(id);
    this.write();
  }

  /**
   * Removes all assets from the local storage and resets the in-memory assets.
   * This method is used internally to clear all stored data.
   */
  removeAll() {
    this.assets = {
      [assetCategoryTypes.CART]: new Map(),
      [assetCategoryTypes.SHOPPING_LIST]: new Map()
    };
    this.cache.clear(); // Clear the cache to prevent stale bag IDs
    this.write();
  }

  /**
   * Retrieves a cart by its type.
   * Searches the cart asset map for a cart matching the specified type.
   * @param {string} type - The type of the cart (e.g., "default", "express").
   * @returns {Object|null} The cart object if found, or null if not found.
   */
  getCartByType(cartType) {
    const cacheKey = `type.${assetCategoryTypes.CART}`;

    if (this.cache.has(cacheKey)) {
      const cachedValue = this.cache.get(cacheKey);
      if (cachedValue !== undefined) {
        return cachedValue;
      }
    }

    const cart = Array.from(this.assets[assetCategoryTypes.CART].values()).find(
      (cart) => {
        return cart.type === cartType;
      }
    );

    // Only set the cache if cart is found
    if (cart !== undefined) {
      this.cache.set(cacheKey, cart);
    }

    return cart; // Returns undefined if no cart matches the type
  }

  /**
   * Retrieves a shopping list by its type.
   * Searches the shopping list asset map for a list matching the specified type.
   * @param {string} type - The type of the shopping list (e.g., "default", "wishlist").
   * @returns {Object|null} The shopping list object if found, or null if not found.
   */
  getShoppingListByType(shoppingListType) {
    const cacheKey = `type.${assetCategoryTypes.SHOPPING_LIST}`;

    if (this.cache.has(cacheKey)) {
      const cachedValue = this.cache.get(cacheKey);
      if (cachedValue !== undefined) {
        return cachedValue;
      }
    }

    const shoppingList = Array.from(
      this.assets[assetCategoryTypes.SHOPPING_LIST].values()
    ).find((shoppinglist) => {
      return shoppinglist.type === shoppingListType;
    });

    if (shoppingList !== undefined) {
      this.cache.set(cacheKey, shoppingList);
    }

    return shoppingList;
  }

  /**
   * Serializes the asset map into a plain object.
   * Converts the Map structure of assets into a serializable object for storage.
   * @param {Map} assetMap - The asset map to serialize.
   * @returns {Object} A plain object representation of the asset map.
   */
  _serializeAssetMap() {
    const serializedData = {
      [assetCategoryTypes.CART]: this.carts,
      [assetCategoryTypes.SHOPPING_LIST]: this.shoppingLists,
      scope: this.scope
    };
    return serializedData;
  }
  /**
   * Writes the current state of assets to the client storage.
   * Converts the in-memory asset maps into serializable objects and persists them.
   */
  write() {
    this.storage.setItem(this.storageKey, this._serializeAssetMap());
  }

  /**
   * Updates a cart asset with the provided payload and type.
   * Adds or updates the cart in the asset map and writes the changes to storage.
   * @param {Object} payload - The payload containing cart data.
   * @param {string} type - The type of the cart (e.g., "default", "express").
   * @throws {Error} If an error occurs while updating the cart.
   */
  _updateCart({ id, encryptedId, channel = channelTypes.DEFAULT }, type) {
    if (!id || !encryptedId)
      throw new Error("Fields missing in Asset extensions");

    try {
      const existingCart = this.assets[assetCategoryTypes.CART].get(id) || {};

      this.assets[assetCategoryTypes.CART].set(id, {
        ...existingCart,
        id,
        encryptedId,
        type,
        channel,
        lastModifiedAt: new Date()
      });

      this.write();
    } catch (error) {
      throw new Error(error?.message);
    }
  }

  /**
   * Updates a shopping list asset with the provided payload and type.
   * Adds or updates the shopping list in the asset map and writes the changes to storage.
   * @param {Object} payload - The payload containing shopping list data.
   * @param {string} type - The type of the shopping list (e.g., "default", "wishlist").
   * @throws {Error} If an error occurs while updating the shopping list.
   */
  _updateShoppingList({ id, channel = channelTypes.DEFAULT }, type) {
    if (!id) throw new Error("Fields missing in Asset extensions");

    try {
      const existingShoppingList =
        this.assets[assetCategoryTypes.SHOPPING_LIST].get(id) || {};

      this.assets[assetCategoryTypes.SHOPPING_LIST].set(id, {
        ...existingShoppingList,
        id,
        type,
        channel,
        lastModifiedAt: new Date()
      });

      this.write();
    } catch (error) {
      throw new Error(error?.message || "Failed to update shopping list");
    }
  }

  /**
   * Updates an asset based on its category.
   * Delegates the update to the appropriate method based on the category type.
   * @param {Object} params - The parameters for updating the asset.
   * @param {string} params.category - The category of the asset (e.g., "CART", "SHOPPING_LIST").
   * @param {Object} params.payload - The payload containing the asset data.
   * @param {string} params.type - The type of the asset.
   * @throws {Error} If the category type is unknown.
   */
  updateAsset({ category, payload, type }) {
    switch (category) {
      case assetCategoryTypes.CART: {
        return this._updateCart(payload, type);
      }
      case assetCategoryTypes.SHOPPING_LIST: {
        return this._updateShoppingList(payload, type);
      }
      default:
        throw new Error(`Unknown category type ,${category}`);
    }
  }

  /**
   * Removes an asset from the specified category.
   * Deletes the asset from the local storage and updates the storage.
   * @param {Object} params - The parameters for removing the asset.
   * @param {string} params.category - The category of the asset (e.g., "CART", "SHOPPING_LIST").
   * @param {Object} params.payload - The payload containing the asset data.
   * @param {string} params.type - The type of the asset.
   * @throws {Error} If the category type is unknown or the asset ID is missing.
   */
  removeAsset({ category, payload, type }) {
    if (!Object.values(assetCategoryTypes).includes(category))
      throw new Error(`Unknown category type ,${category}`);

    // checks if the asset id key is present
    if (!payload?.id)
      throw new Error(`Id is required param to remove asset, ${category}`);

    this.assets[category].delete(payload.id);
    this.write();
  }

  /**
   * Retrieves the bag ID for a given type.
   * @param {string} type - The type of the bag (e.g., "default", "express").
   * @returns {string} The bag ID for the specified type, or an empty string if not found.
   * @throws {Error} If an error occurs while fetching the bag ID.
   */
  getBagIdByType(type) {
    try {
      const cart = this.getCartByType(type);
      return cart?.id || "";

      // if no CommerceAsset in clientstorage & no carts of the type then
      // To do : use fallback to fetch the ngc asset, store it & fetch back the default bagId
      // fallback code here, this will be done as part of anothter ticket
    } catch (error) {
      throw new Error(error?.message);
    }
  }

  /**
   * Retrieves the shopping list ID for a given type.
   * @param {string} type - The type of the shopping list (e.g., "default", "wishlist").
   * @returns {string} The shopping list ID for the specified type, or an empty string if not found.
   * @throws {Error} If an error occurs while fetching the shopping list ID.
   */
  getShoppingListIdByType(type) {
    try {
      const shoppingList = this.getShoppingListByType(type);
      return shoppingList?.id || "";
    } catch (error) {
      throw new Error(
        error?.message || "Error occured in fetching Shopping List Id"
      );
    }
  }
  /**
   * Clears all commerce-related assets from the client storage.
   * Typically used during logout to remove all stored assets.
   * @throws {Error} If an error occurs while clearing the assets.
   */
  // in case of logout,
  clearCommerceAsset() {
    try {
      this.removeAll();
    } catch (error) {
      throw new Error(error?.message);
    }
  }
}
